<script>
import { onMount, onDestroy } from "svelte";
import { playerState } from "@app/store/playerStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { styleState } from "@app/store/styleStore.svelte.js";
import { createGvPlayerAdapter } from "@app/lib/player/gvPlayerAdapter.js";
import ScsPlayer from "@app/lib/scs-player.js";
import ClosedCaptionPreview from "./ClosedCaptionPreview.svelte";
import WatermarkOverlay from "./WatermarkOverlay.svelte";
import SubtitlePreview from "./SubtitlePreview.svelte";
import TranscriptPreview from "./TranscriptPreview.svelte";
import AdPreview from "./AdPreview.svelte";
import AdrPreview from "./AdrPreview.svelte";
import StemPreview from "./StemPreview.svelte";
import { isAudioGroupType } from "@app/lib/eventGroupTypes.js";
import * as m from "@app/paraglide/messages.js";

let iframeEl;
let adapter = $state(null);
/** Non-reactive handle for onDestroy cleanup */
let adapterRef = null;
let lastBaseUrl = "";
let lastMaterialId = "";

function handleComponentError(error) {
    console.error("Component Error:", error);
}

onMount(() => {
    const next = createGvPlayerAdapter({
        embedUrl: playerState.flxEmbedUrl,
        materialId: playerState.flxMaterialId,
        playerBaseUrl: playerState.flxPlayerBaseUrl,
    });
    adapterRef = next;
    window.player = next;

    if (!iframeEl) return;

    const instance = new ScsPlayer(
        iframeEl,
        playerState.flxPlayerBaseUrl,
    );
    next.attachRealPlayer(instance);
    if (playerState.flxMaterialId) {
        instance.load(playerState.flxMaterialId);
    }

    lastBaseUrl = playerState.flxPlayerBaseUrl;
    lastMaterialId = playerState.flxMaterialId;
    adapter = next;
});

onDestroy(() => {
    adapterRef?.destroy();
    if (window.player === adapterRef) {
        window.player = null;
    }
    adapterRef = null;
    adapter = null;
});

$effect(() => {
    const embedUrl = playerState.flxEmbedUrl;
    const materialId = playerState.flxMaterialId;
    const playerBaseUrl = playerState.flxPlayerBaseUrl;

    if (!adapter || !iframeEl) return;

    adapter.setEmbedUrl(embedUrl, materialId, playerBaseUrl);

    if (playerBaseUrl !== lastBaseUrl) {
        adapter.attachRealPlayer(null);
        const instance = new ScsPlayer(iframeEl, playerBaseUrl);
        adapter.attachRealPlayer(instance);
        if (materialId) {
            instance.load(materialId);
        }
        lastBaseUrl = playerBaseUrl;
        lastMaterialId = materialId;
        return;
    }

    if (materialId !== lastMaterialId) {
        adapter.unloadMaterial();
        if (materialId) {
            adapter.loadMaterial(materialId);
        }
        lastMaterialId = materialId;
    }
});
</script>

<div
    class="gv-player"
    style="aspect-ratio: {projectState?.media?.aspectRatio ||
        '16/9'}; background-color: black; position: relative; width: 100%"
>
    <iframe
        bind:this={iframeEl}
        id="flx-player"
        class="gv-player__frame"
        title={m.player_video_player_aria_label()}
        allow="autoplay; fullscreen"
        sandbox="allow-scripts allow-same-origin allow-popups"
        allowfullscreen
    ></iframe>
    <div class="gv-player__overlay">
        <svelte:boundary onerror={handleComponentError}>
            <WatermarkOverlay></WatermarkOverlay>
        </svelte:boundary>
        <svelte:boundary onerror={handleComponentError}>
            <StemPreview></StemPreview>
        </svelte:boundary>
        {#if eventGroupState[projectState.selected] && isAudioGroupType(eventGroupState[projectState.selected].type) && styleState.adrPreview}
            <svelte:boundary onerror={handleComponentError}>
                <AdrPreview></AdrPreview>
            </svelte:boundary>
        {:else if eventGroupState[projectState.selected] && isAudioGroupType(eventGroupState[projectState.selected].type)}
            <svelte:boundary onerror={handleComponentError}>
                <AdPreview></AdPreview>
            </svelte:boundary>
        {:else if eventGroupState[projectState.selected] && eventGroupState[projectState.selected].type === "transcription"}
            <svelte:boundary onerror={handleComponentError}>
                <TranscriptPreview></TranscriptPreview>
            </svelte:boundary>
        {:else if eventGroupState[projectState.selected] && styleState.ccMode}
            <svelte:boundary onerror={handleComponentError}>
                <ClosedCaptionPreview></ClosedCaptionPreview>
            </svelte:boundary>
        {:else if eventGroupState[projectState.selected]}
            <svelte:boundary onerror={handleComponentError}>
                <SubtitlePreview></SubtitlePreview>
            </svelte:boundary>
        {/if}
    </div>
</div>

<style>
.gv-player {
    position: relative;
    width: 100%;
    background-color: black;
}

.gv-player__frame {
    display: block;
    width: 100%;
    height: 100%;
    border: none;
    position: absolute;
    inset: 0;
}

.gv-player__overlay {
    pointer-events: none;
    position: absolute;
    inset: 0;
    z-index: 1;
}
</style>
